import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

type PaymentStatus = 'approved' | 'pending' | 'failure' | 'loading';

const STATUS_CONFIG: Record<PaymentStatus, {
  icon: string;
  iconColor: string;
  bgColor: string;
  title: string;
  description: string;
  actionLabel: string;
  actionPath: string;
}> = {
  loading: {
    icon: 'ri-loader-4-line',
    iconColor: 'text-gray-400',
    bgColor: 'bg-gray-50',
    title: 'Verificando pagamento...',
    description: 'Aguarde enquanto confirmamos seu pagamento.',
    actionLabel: '',
    actionPath: '',
  },
  approved: {
    icon: 'ri-check-double-line',
    iconColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    title: 'Pagamento aprovado!',
    description:
      'Sua assinatura foi ativada com sucesso. Todos os recursos premium já estão disponíveis na sua conta.',
    actionLabel: 'Ir para o dashboard',
    actionPath: '/dashboard',
  },
  pending: {
    icon: 'ri-time-line',
    iconColor: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    title: 'Pagamento em análise',
    description:
      'Seu pagamento foi recebido e está sendo processado. Assim que confirmado, seu plano será atualizado automaticamente.',
    actionLabel: 'Voltar ao painel',
    actionPath: '/dashboard',
  },
  failure: {
    icon: 'ri-close-circle-line',
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
    title: 'Pagamento não aprovado',
    description:
      'Não foi possível processar seu pagamento. Verifique seus dados e tente novamente.',
    actionLabel: 'Tentar novamente',
    actionPath: '/perfil?tab=plano',
  },
};

const PagamentoRetornoPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<PaymentStatus>('loading');
  const [planName, setPlanName] = useState<string>('');
  const [verifying, setVerifying] = useState(true);
  const [pollCount, setPollCount] = useState(0);

  const queryParam = searchParams.get('status') as PaymentStatus | null;

  const verifyPaymentStatus = useCallback(async () => {
    if (!user) return;
    try {
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('*, plans(name)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (subscription?.status === 'active' && subscription?.subscription_type === 'paid') {
        const pName = (subscription as { plans?: { name?: string } }).plans?.name ?? 'Premium';
        setPlanName(pName);
        setStatus('approved');
        setVerifying(false);
        return true;
      }
    } catch (err) {
      console.warn('[PagamentoRetorno] verify error:', err);
    }
    return false;
  }, [user]);

  useEffect(() => {
    if (!queryParam) {
      setStatus('failure');
      setVerifying(false);
      return;
    }

    if (queryParam === 'failure') {
      setStatus('failure');
      setVerifying(false);
      return;
    }

    // For approved/pending: verify in DB (webhook may take a few seconds)
    let isMounted = true;
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      if (!isMounted) return;
      attempts++;
      const confirmed = await verifyPaymentStatus();
      if (!confirmed && attempts < maxAttempts) {
        setPollCount(attempts);
        setTimeout(poll, 2000);
      } else if (!confirmed) {
        // Fallback: trust what MP told us
        if (isMounted) {
          setStatus(queryParam === 'approved' ? 'approved' : 'pending');
          setVerifying(false);
        }
      } else {
        setVerifying(false);
      }
    };

    poll();

    return () => {
      isMounted = false;
    };
  }, [queryParam, verifyPaymentStatus]);

  const cfg = STATUS_CONFIG[status];

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100">
          {/* Top band */}
          <div
            className={`h-2 w-full ${
              status === 'approved'
                ? 'bg-emerald-500'
                : status === 'pending'
                ? 'bg-yellow-400'
                : status === 'failure'
                ? 'bg-red-400'
                : 'bg-gray-200'
            }`}
          />

          <div className="px-8 py-10 text-center">
            {/* Icon */}
            <div
              className={`w-20 h-20 flex items-center justify-center rounded-3xl mx-auto mb-6 ${cfg.bgColor}`}
            >
              <i
                className={`${cfg.icon} text-4xl ${cfg.iconColor} ${
                  status === 'loading' || verifying ? 'animate-spin' : ''
                }`}
              />
            </div>

            <h1 className="text-xl font-700 text-gray-900 mb-3">{cfg.title}</h1>
            <p className="text-sm text-gray-500 leading-relaxed mb-2">{cfg.description}</p>

            {status === 'approved' && planName && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full">
                <i className="ri-vip-crown-fill text-amber-500 text-sm" />
                <span className="text-sm font-600 text-emerald-700">Plano {planName} ativo</span>
              </div>
            )}

            {verifying && status === 'loading' && (
              <p className="text-xs text-gray-400 mt-4">
                Confirmando no servidor... ({pollCount > 0 ? `tentativa ${pollCount}` : 'aguardando'})
              </p>
            )}

            {/* Features unlocked (approved only) */}
            {status === 'approved' && (
              <div className="mt-6 grid grid-cols-2 gap-2 text-left">
                {[
                  'Contas ilimitadas',
                  'Cartões ilimitados',
                  'Metas ilimitadas',
                  'Relatórios avançados',
                  'Exportação CSV/PDF',
                  'Suporte prioritário',
                ].map((f) => (
                  <div key={f} className="flex items-center gap-2 bg-emerald-50 rounded-xl p-2.5">
                    <i className="ri-check-line text-emerald-500 text-xs flex-shrink-0" />
                    <span className="text-xs text-emerald-700 font-500">{f}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Pending info */}
            {status === 'pending' && (
              <div className="mt-4 bg-yellow-50 border border-yellow-100 rounded-2xl p-4 text-left">
                <p className="text-xs font-700 text-yellow-700 mb-1 flex items-center gap-1.5">
                  <i className="ri-information-line" />O que acontece agora?
                </p>
                <ul className="text-xs text-yellow-600 space-y-1 pl-4 list-disc">
                  <li>Seu pagamento está sendo confirmado pelo banco</li>
                  <li>Assim que aprovado, seu plano é atualizado automaticamente</li>
                  <li>Você receberá uma notificação quando o plano for liberado</li>
                  <li>Normalmente leva de alguns minutos a 1 hora</li>
                </ul>
              </div>
            )}

            {/* Failure help */}
            {status === 'failure' && (
              <div className="mt-4 bg-red-50 border border-red-100 rounded-2xl p-4 text-left">
                <p className="text-xs font-700 text-red-600 mb-1">Possíveis motivos:</p>
                <ul className="text-xs text-red-500 space-y-1 pl-4 list-disc">
                  <li>Dados do cartão incorretos</li>
                  <li>Saldo ou limite insuficiente</li>
                  <li>Cartão bloqueado para compras online</li>
                  <li>Limite de tentativas atingido</li>
                </ul>
              </div>
            )}

            {/* Action button */}
            {!verifying && cfg.actionLabel && (
              <button
                onClick={() => navigate(cfg.actionPath)}
                className={`mt-6 w-full py-3 rounded-xl font-700 text-sm cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2 ${
                  status === 'approved'
                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                    : status === 'pending'
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                    : 'bg-red-500 hover:bg-red-600 text-white'
                }`}
              >
                {status === 'approved' && <i className="ri-rocket-line" />}
                {status === 'pending' && <i className="ri-home-line" />}
                {status === 'failure' && <i className="ri-refresh-line" />}
                {cfg.actionLabel}
              </button>
            )}

            {status === 'failure' && (
              <button
                onClick={() => navigate('/dashboard')}
                className="mt-2 w-full py-2.5 rounded-xl text-gray-500 text-sm cursor-pointer hover:bg-gray-50 font-500"
              >
                Ir para o dashboard
              </button>
            )}
          </div>
        </div>

        {/* Security note */}
        <div className="flex items-center justify-center gap-2 mt-4">
          <i className="ri-shield-check-line text-gray-300 text-sm" />
          <p className="text-xs text-gray-400">Pagamento processado com segurança pelo Mercado Pago</p>
        </div>
      </div>
    </div>
  );
};

export default PagamentoRetornoPage;
